// List of functions available here
// -> getFirebaseAuthToken
// -> isTokenExpired
// -> getLatLong
// -> getGeoHash
// -> createRazorpayAccountRout
// -> getFutureCycles
// -> logAction
import { infoToRazorpay } from "../Subscriptions/types/AccountType.ts";
import axios from "axios";
import { format } from 'date-fns';
import { getAuth } from "firebase/auth";
import { SECRETS_ENV } from "../../Config/firebase.ts";
import { db, firebase } from "../../Config/firebase.ts";
import { setDoc, Timestamp } from "firebase/firestore";
import { FirebaseError } from "firebase/app";
import { RootState, useAppSelector } from "../../state/state.tsx";

interface ActionLog {
    action: string;
    platform: string;
    timestamp: Timestamp;
    context: Record<string, any>;
    selectedSubscriptions?: string[];
    livesubscriptionId?: string;
    performedBy: {
        spId: string;
        name: string;
    };
}

export const logAction = (actionData: ActionLog, spId: string) => {

    const docRef = firebase.doc(firebase.collection(db, `ServiceProviders/${spId}/ActionLogs`));
    setDoc(docRef, {
        ...actionData,
    }).catch((error: FirebaseError) => {
        console.error('Error logging action:', error);
    });

};

export const getFirebaseAuthToken = async () => {

    try {

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        let token: string = await getAuth().currentUser?.getIdToken()
        if (!isTokenExpired(token)) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            token = await getAuth().currentUser?.getIdToken()

        }
        return token

    } catch (error) {
        console.error('Error fetching token:', error);
    }
};

function isTokenExpired(token: string) {
    if (!token) return false;

    const parts = token.split('.');
    if (parts.length !== 3) {
        throw new Error('Invalid token');
    }

    const payload = JSON.parse(atob(parts[1]));
    const expiry = payload.exp || 0;
    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    // Consider the token expired if it expires within 5 minutes (300 seconds)
    return expiry > now + 300;
}


const GEO_LOC_API = import.meta.env.VITE_GEOLOCATION_API_KEY || process.env.VITE_GEOLOCATION_API_KEY

export const getLatLong = async (placeId: string) => {
    const response = await fetch(`https://gentle-sea-95081-32ec2d382296.herokuapp.com/https://maps.googleapis.com/maps/api/place/details/json?placeid=${placeId}&language=en&key=${GEO_LOC_API}`)
    const json = await response.json()
    const geometry = {
        lat: null,
        lng: null
    }
    const geometry_resp = json.result.geometry.location

    geometry.lat = geometry_resp.lat
    geometry.lng = geometry_resp.lng
    return geometry
}

export function getGeoHash(latLong: any) {
    if (!latLong) return null;

    const base32 = '0123456789bcdefghjkmnpqrstuvwxyz';
    const precision = 10;
    let minLat = -90.0, maxLat = 90.0;
    let minLon = -180.0, maxLon = 180.0;
    const geohash = [];

    for (let i = 0; i < precision; i++) {
        let bit = 0;
        for (let j = 4; j >= 0; j--) {
            const midLat = (minLat + maxLat) / 2.0;
            const midLon = (minLon + maxLon) / 2.0;

            if (j % 2 === 0) {
                if (latLong.longitude > midLon) {
                    bit |= (1 << j);
                    minLon = midLon;
                } else {
                    maxLon = midLon;
                }
            } else {
                if (latLong.latitude > midLat) {
                    bit |= (1 << j);
                    minLat = midLat;
                } else {
                    maxLat = midLat;
                }
            }
        }
        geohash.push(base32[bit]);
    }

    return geohash.join('');
}

export async function createRazorpayAccountRoute(reqDoc: infoToRazorpay) {
    try {
        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${SECRETS_ENV.API_END_POINT}/api/registration`,
            headers: {
                'Content-Type': 'application/json'
            },
            data: reqDoc
        };

        const response = await axios.request(config);

        return response.data; // Return the data from the request

    } catch (error) {

        return "error" // Throw the error to handle it elsewhere if needed
    }
}


enum BillingType {
    Upfront,
    Regular,
}


export function getFutureCycles(billingType?: BillingType): string[] {
    // Get today's date
    const currentDate = new Date();


    // Determine the starting month based on the billing type
    // const startMonthOffset = billingType === BillingType.Upfront ? 1 : 0;
    const startMonthOffset = 0

    // Create a list to hold the cycle months
    const cycles: string[] = [];

    // Generate the next 6 months including the current month if required
    for (let i = startMonthOffset; i < 6 + startMonthOffset; i++) {
        const futureDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + i);
        const formattedDate = format(futureDate, 'MMMM yyyy');
        cycles.push(formattedDate);
    }

    return cycles;
}


export function getBankAccountImage(bankCode: string) {
    switch (bankCode) {
        case 'KKBK':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/160b3ko2txkj/KOTAK-PNG.png';
        case 'UTIB':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/wm3ou1p7ny9k/AXIS-PNG.png';
        case 'CNRB':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/tsfwp1mrwrnv/CANARA-PNG.png';
        case 'ICIC':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/r6yy78w3o125/ICICI-PNG.png';
        case 'HDFC':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/7gn2cjt9ipv7/HDFC-PNG.png';
        case 'SBIN':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/38r993ml8a3j/SBI-PNG.png';
        case 'KARB':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/sejf7iazj67d/Karnataka-PNG.png';
        case 'YESB':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/9awbbq4q5q4w/YesBank-PNG3.png';
        case 'FDRL':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/8mw0f3whyfxd/Federab_Bank_PNG.png';
        case 'DBSS':
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/9ht6eew52kcx/DBS-Icon.png';
        default:
            return 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/saksham-provider-develop-e0dw3z/assets/vvhb1ggzrfgp/Bank-PNG3.png';
    }
}


export const formatDateAsDDMMYYYY = (date: Date) => {
    return date.toLocaleDateString('en-GB'); // Format as DD/MM/YYYY
};

export function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
}